import { useState, type FormEvent } from 'react';
import { PageLoader } from '@/components/ui/spinner';
import { useLocation } from 'wouter';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Play,
  Square,
  Trash2,
  QrCode,
  CircleCheck,
  CircleX,
  Loader2,
} from 'lucide-react';
import { api, ApiCallError } from '@/lib/api';
import { useCurrentAdmin } from '@/hooks/use-current-admin';
import { AdminShell } from '@/components/admin-shell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PhoneInput, { toInternationalPhoneDigits, isValidPhone } from '@/components/ui/phone-input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FieldError } from '@/components/ui/field-error';

interface Bot {
  id: string;
  platform: 'whatsapp_baileys' | 'whatsapp_official' | 'telegram';
  displayName: string;
  phoneNumber: string | null;
  username: string | null;
  status: 'disconnected' | 'pairing' | 'connected' | 'error';
  isDefault: boolean;
  hasToken: boolean;
  lastQrAt: string | null;
  lastConnectedAt: string | null;
  lastError: string | null;
  runtimeStatus: 'disconnected' | 'pairing' | 'connected' | 'error';
  runtimeQr: string | null;
}

const PLATFORMS: Record<Bot['platform'], string> = {
  whatsapp_baileys: 'WhatsApp (Baileys)',
  whatsapp_official: 'WhatsApp Business API',
  telegram: 'Telegram',
};

export function MessagingPage() {
  const [, navigate] = useLocation();
  const { data: me, isLoading: loadingMe } = useCurrentAdmin();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [qrBotId, setQrBotId] = useState<string | null>(null);
  const [deleteBotId, setDeleteBotId] = useState<{ id: string; name: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['messaging-bots'],
    queryFn: () => api<{ items: Bot[] }>('/api/admin/messaging/bots'),
    enabled: !!me,
    refetchInterval: (q) => {
      const items = (q.state.data as { items?: Bot[] } | undefined)?.items ?? [];
      return items.some((b) => b.runtimeStatus === 'pairing') ? 2000 : 10000;
    },
  });

  const startMutation = useMutation({
    mutationFn: (id: string) => api(`/api/admin/messaging/bots/${id}/start`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['messaging-bots'] }),
  });
  const stopMutation = useMutation({
    mutationFn: (id: string) => api(`/api/admin/messaging/bots/${id}/stop`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['messaging-bots'] }),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/api/admin/messaging/bots/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['messaging-bots'] }),
  });

  if (loadingMe) return <PageLoader />;
  if (!me) {
    navigate('/login');
    return null;
  }
  if (me.role !== 'superadmin') {
    return (
      <AdminShell>
        <div className="text-destructive">Solo superadmins pueden gestionar bots de mensajería.</div>
      </AdminShell>
    );
  }

  const liveBot = qrBotId ? data?.items.find((b) => b.id === qrBotId) ?? null : null;

  return (
    <AdminShell>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Mensajería</h1>
          <p className="text-sm text-muted-foreground">
            Bots de WhatsApp y Telegram que reciben facturas de los clientes
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>
          <Plus />
          Nuevo bot
        </Button>
      </div>

      <div className="rounded-lg border border-border bg-background shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Bot</TableHead>
              <TableHead>Plataforma</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Conexión</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                  Cargando…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && data?.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-12 text-center text-muted-foreground">
                  Sin bots todavía.{' '}
                  <Button variant="link" size="sm" onClick={() => setShowCreate(true)}>
                    Crear el primero
                  </Button>
                </TableCell>
              </TableRow>
            )}
            {data?.items.map((b) => (
              <TableRow key={b.id}>
                <TableCell>
                  <div className="font-medium text-foreground">{b.displayName}</div>
                  {b.phoneNumber && (
                    <div className="text-xs text-muted-foreground font-mono">{b.phoneNumber}</div>
                  )}
                  {b.username && <div className="text-xs text-muted-foreground">@{b.username}</div>}
                  {b.isDefault && (
                    <div className="mt-1">
                      <Badge variant="default">default</Badge>
                    </div>
                  )}
                </TableCell>
                <TableCell>{PLATFORMS[b.platform]}</TableCell>
                <TableCell className="align-top">
                  <StatusBadge status={b.runtimeStatus} />
                  {b.lastError && (
                    <div className="mt-1 max-w-[320px] whitespace-normal break-words text-xs text-destructive">
                      {b.lastError}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground text-xs">
                  {b.lastConnectedAt
                    ? `Conectado ${new Date(b.lastConnectedAt).toLocaleString('es-PY')}`
                    : '—'}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {b.runtimeStatus === 'pairing' && b.runtimeQr && b.platform === 'whatsapp_baileys' && (
                      <Button size="sm" onClick={() => setQrBotId(b.id)}>
                        <QrCode />
                        Ver QR
                      </Button>
                    )}
                    {(b.runtimeStatus === 'disconnected' || b.runtimeStatus === 'error') && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => startMutation.mutate(b.id)}
                        disabled={startMutation.isPending}
                      >
                        <Play />
                        Iniciar
                      </Button>
                    )}
                    {(b.runtimeStatus === 'connected' || b.runtimeStatus === 'pairing') && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => stopMutation.mutate(b.id)}
                        disabled={stopMutation.isPending}
                      >
                        <Square />
                        Detener
                      </Button>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setDeleteBotId({ id: b.id, name: b.displayName })}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <CreateBotDialog open={showCreate} onOpenChange={setShowCreate} />

      <AlertDialog open={!!deleteBotId} onOpenChange={(open) => !open && setDeleteBotId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar bot</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Seguro que querés eliminar el bot "{deleteBotId?.name}"? La sesión persistida y la
              conexión se pierden. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteBotId) {
                  deleteMutation.mutate(deleteBotId.id);
                  setDeleteBotId(null);
                }
              }}
              className="bg-destructive hover:opacity-90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!liveBot} onOpenChange={(open) => !open && setQrBotId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Escaneá el QR con WhatsApp</DialogTitle>
            <DialogDescription>{liveBot?.displayName}</DialogDescription>
          </DialogHeader>
          {liveBot && (
            <div className="text-center space-y-3">
              {liveBot.runtimeQr ? (
                <>
                  <img
                    src={liveBot.runtimeQr}
                    alt="QR"
                    className="mx-auto rounded-md border border-border"
                    width={320}
                  />
                  <p className="text-xs text-muted-foreground">
                    Abrí WhatsApp en el teléfono del bot → Menú → Dispositivos vinculados → Vincular
                    un dispositivo → escaneá este código.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    El QR se renueva automáticamente cada ~20 segundos.
                  </p>
                </>
              ) : liveBot.runtimeStatus === 'connected' ? (
                <p className="text-success font-medium inline-flex items-center gap-2 justify-center">
                  <CircleCheck className="size-5" />
                  Conectado
                </p>
              ) : (
                <p className="text-muted-foreground inline-flex items-center gap-2 justify-center">
                  <Loader2 className="size-4 animate-spin" />
                  Esperando QR…
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}

function StatusBadge({ status }: { status: Bot['runtimeStatus'] }) {
  const cfg = {
    connected: { variant: 'success' as const, icon: CircleCheck, label: 'Conectado' },
    pairing: { variant: 'info' as const, icon: Loader2, label: 'Vinculando' },
    disconnected: { variant: 'secondary' as const, icon: CircleX, label: 'Desconectado' },
    error: { variant: 'destructive' as const, icon: CircleX, label: 'Error' },
  }[status];
  const Icon = cfg.icon;
  return (
    <Badge variant={cfg.variant}>
      <Icon className={cfg.label === 'Vinculando' ? 'size-3 animate-spin' : 'size-3'} />
      {cfg.label}
    </Badge>
  );
}

function CreateBotDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    platform: 'whatsapp_baileys' as Bot['platform'],
    displayName: 'WhatsApp Baileys principal',
    username: '',
    telegramToken: '',
    isDefault: true,
  });
  const [phoneCountry, setPhoneCountry] = useState('PY');
  const [phoneNational, setPhoneNational] = useState('');
  const [issues, setIssues] = useState<Record<string, string[]>>({});
  // Número del bot es opcional y solo aplica a Baileys: válido si la plataforma
  // no es Baileys, si está vacío, o si libphonenumber lo acepta.
  const phoneValid =
    form.platform !== 'whatsapp_baileys' ||
    phoneNational.trim() === '' ||
    isValidPhone(phoneCountry, phoneNational);

  const mutation = useMutation({
    mutationFn: () =>
      api('/api/admin/messaging/bots', {
        method: 'POST',
        body: JSON.stringify({
          platform: form.platform,
          displayName: form.displayName,
          phoneNumber:
            form.platform === 'whatsapp_baileys' && phoneNational.trim()
              ? toInternationalPhoneDigits(phoneCountry, phoneNational)
              : null,
          username: form.username || null,
          isDefault: form.isDefault,
          telegramToken: form.platform === 'telegram' ? form.telegramToken.trim() : undefined,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['messaging-bots'] });
      onOpenChange(false);
      setIssues({});
    },
    onError: (err) => {
      if (err instanceof ApiCallError && err.body.issues) setIssues(err.body.issues);
      else setIssues({ _form: ['No se pudo crear'] });
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setIssues({});
    mutation.mutate();
  }

  function setPlatform(p: Bot['platform']) {
    const defaults: Record<Bot['platform'], string> = {
      whatsapp_baileys: 'WhatsApp Baileys principal',
      whatsapp_official: 'WhatsApp Business API',
      telegram: 'Telegram bot',
    };
    setForm({ ...form, platform: p, displayName: defaults[p] });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nuevo bot de mensajería</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label required>Plataforma</Label>
            <Select value={form.platform} onValueChange={(v) => setPlatform(v as Bot['platform'])}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="whatsapp_baileys">WhatsApp (Baileys, no oficial)</SelectItem>
                <SelectItem value="telegram">Telegram</SelectItem>
                <SelectItem value="whatsapp_official" disabled>
                  WhatsApp Business API (próximamente)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label required>Nombre interno</Label>
            <Input
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            />
            <FieldError error={issues.displayName} />
          </div>

          {form.platform === 'whatsapp_baileys' && (
            <div className="space-y-1.5">
              <Label>Número (opcional, solo referencia)</Label>
              <PhoneInput
                showLabel={false}
                countryCode={phoneCountry}
                phoneNumber={phoneNational}
                onCountryCodeChange={setPhoneCountry}
                onPhoneNumberChange={setPhoneNational}
                placeholder="0991 123-456"
              />
              {!phoneValid && (
                <p className="text-xs text-destructive">Número de teléfono inválido.</p>
              )}
            </div>
          )}

          {form.platform === 'telegram' && (
            <div className="space-y-2">
              <div className="space-y-1.5">
                <Label required>Bot Token</Label>
                <Input
                  type="password"
                  placeholder="123456789:ABCdefGHI..."
                  value={form.telegramToken}
                  onChange={(e) => setForm({ ...form, telegramToken: e.target.value })}
                  className="font-mono text-xs"
                />
                <FieldError error={issues.telegramToken} />
              </div>
              <div className="rounded-md bg-accent border border-border p-3 text-xs text-foreground space-y-1">
                <p className="font-semibold">Cómo obtener el token:</p>
                <ol className="list-decimal list-inside space-y-0.5 text-muted-foreground">
                  <li>
                    Abrí Telegram y buscá <span className="font-mono">@BotFather</span>
                  </li>
                  <li>
                    Mandá <span className="font-mono">/newbot</span> y seguí las instrucciones
                  </li>
                  <li>
                    Te devuelve un token tipo <span className="font-mono">123456:ABC-DEF...</span>
                  </li>
                  <li>Pegalo arriba (lo guardamos en la DB del servidor)</li>
                </ol>
              </div>
            </div>
          )}

          <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
            <Checkbox
              checked={form.isDefault}
              onCheckedChange={(v) => setForm({ ...form, isDefault: v === true })}
            />
            Default para esta plataforma (mensajes huérfanos van acá)
          </label>

          {issues._form && <p className="text-sm text-destructive">{issues._form[0]}</p>}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending || !phoneValid}>
              {mutation.isPending ? 'Creando…' : 'Crear'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
